import { useState, useEffect, useRef } from "react";
import useAuth from "../../Users/hooks/useAuth";
import AccessInterface from "../../Users/AccessInterface";
import UserProfile from "../../Users/UserProfile";
import BookCase from "../../Books/BookCase";
import Audiobook from "./Audiobook";
import Videobook from "./Videobook";
import Collection from "./Collection";
import CommunityCommentsBrowser from "./SnapsWise/comments/CommunityCommentsBrowser";
import { Link } from "react-router-dom";
import YourPresentations from "../../presentations/YourPresentations";
import HowToPage from "./HowToPage";
import About from "./About";
import { getLanguageFlag } from "./flags";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function HeaderButton({
  children,
  icon,
  onClick,
  active = false,
  primary = false,
  mobile = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-2 font-bold transition-all duration-200",
        mobile
          ? "w-full rounded-2xl px-4 py-3 text-left text-sm"
          : "rounded-2xl px-4 py-2.5 text-sm",
        primary
          ? "border border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:-translate-y-0.5 hover:bg-blue-700"
          : active
            ? "border border-slate-300 bg-slate-100 text-slate-950"
            : "border border-slate-200 bg-white text-slate-700 shadow-sm hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
      )}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="truncate">{children}</span>
    </button>
  );
}

function UserButton({ auth, message, onProfile }) {
  return (
    <button
      type="button"
      onClick={onProfile}
      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50"
    >
      {message ? (
        <span className="animate-pulse font-bold text-slate-700">
          {message}
        </span>
      ) : (
        <>
          <span className="h-2 w-2 rounded-full bg-blue-600" />
          <span>{auth.username || auth.email}</span>
        </>
      )}
    </button>
  );
}

function GuestWelcomeBlock({ onRequireLogin, onRegister, onHowTo, onAbout }) {
  return (
    <div className="relative overflow-hidden rounded-[34px] border border-blue-100 bg-gradient-to-br from-white via-blue-50/40 to-slate-50 p-6 shadow-sm md:p-8">
      <div className="absolute -right-20 -top-16 hidden h-56 w-56 rounded-full bg-blue-100/90 blur-3xl md:block" />
      <div className="absolute bottom-0 left-1/3 hidden h-28 w-56 rounded-full bg-white/80 blur-3xl xl:block" />

      <div className="relative grid gap-8 xl:grid-cols-[1fr_0.48fr] xl:items-center">
        <div className="max-w-4xl">
          <div className="inline-flex rounded-full border border-blue-100 bg-white/85 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-blue-600 shadow-sm">
            Pronunciation practice
          </div>

          <h1 className="mt-4 max-w-3xl text-3xl font-black tracking-tight text-slate-950 md:text-4xl">
            Practise pronunciation with real speech.
          </h1>

          <p className="mt-4 max-w-2xl text-base font-semibold leading-7 text-slate-600">
            Choose a text, listen carefully, repeat aloud, and notice rhythm,
            stress, and sound details.
          </p>

          <div className="mt-7 grid max-w-4xl gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white/85 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700">▶</span>
                <div className="text-sm font-black text-slate-950">
                  Follow the model
                </div>
              </div>

              <div className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                Real speech as your guide.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/85 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700">〰</span>
                <div className="text-sm font-black text-slate-950">
                  Practise aloud
                </div>
              </div>

              <div className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                Repeat phrases with rhythm and pitch.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/85 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700">◌</span>
                <div className="text-sm font-black text-slate-950">
                  Get feedback
                </div>
              </div>

              <div className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                Save progress and ask others to listen.
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-white/80 bg-white/90 p-5 shadow-sm backdrop-blur">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-600">
            Start as guest
          </div>

          <p className="mt-2 text-xl font-black leading-tight text-slate-950">
            Start practising freely.
          </p>

          <p className="mt-3 text-sm font-semibold leading-6 text-slate-500">
            Create an account to keep your progress, get personal drills, and
            ask the community for feedback.
          </p>

          <div className="mt-5 flex flex-col gap-3">
            <button
              type="button"
              onClick={onRegister || onRequireLogin}
              className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700"
            >
              Create account
            </button>

            <button
              type="button"
              onClick={onRequireLogin}
              className="inline-flex items-center justify-center rounded-2xl border border-blue-200 bg-white px-5 py-3 text-sm font-black text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-50"
            >
              Sign in
            </button>

            <div className="mt-1 flex items-center justify-center gap-4 text-sm font-black">
              <button
                type="button"
                onClick={onHowTo}
                className="text-blue-700 transition hover:text-blue-900"
              >
                How it works
              </button>

              <span className="text-slate-300">·</span>

              <button
                type="button"
                onClick={onAbout}
                className="text-slate-500 transition hover:text-slate-900"
              >
                About
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoggedInHeroBlock({ onCommunity }) {
  return (
    <div className="rounded-[32px] border border-blue-100 bg-gradient-to-br from-white via-blue-50/40 to-violet-50/30 p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
            Collections
          </div>

          <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
            Choose a language
          </h1>

          <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
            Pick a book or course, practise pronunciation, request human
            feedback, discuss tricky parts, and learn together with fellow
            learners.
          </p>
        </div>

        <button
          type="button"
          onClick={onCommunity}
          className="inline-flex items-center rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700"
        >
          Join the discussion
        </button>
      </div>
    </div>
  );
}

const Library = ({ isGuest: isGuestProp, onRequireLogin, onRegister }) => {
  const { auth, setAuth } = useAuth();

  const isGuest =
    typeof isGuestProp === "boolean"
      ? isGuestProp
      : !auth?.username || auth?.username === "guest" || auth?.isGuest === true;

  const [adminPage, setAdmin] = useState(false);
  const [howtoPage, setHowTo] = useState(false);
  const [aboutPage, setAbout] = useState(false);
  const [creatorPage, setCreator] = useState(false);
  const [presentationsPage, setPresentations] = useState(false);
  const [communityCommentsPage, setCommunityCommentsPage] = useState(false);
  const [userProfile, setProfile] = useState(false);

  const [message, setMessage] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState();
  const [languages, setLanguages] = useState();
  const [error, setError] = useState();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const itemsRef = useRef(null);

  const safeOpenLogin = () => {
    if (onRequireLogin) {
      onRequireLogin();
      return;
    }

    setMessage("Please sign in");
    setTimeout(() => setMessage(null), 1000);
  };

  const safeOpenRegister = () => {
    if (onRegister) {
      onRegister();
      return;
    }

    safeOpenLogin();
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getSubjects = async () => {
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/subjects/list-books`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        setError("Server problem, check later.");
        return;
      }

      const json = await response.json();
      setSubjects(json);
    } catch (err) {
      console.error("getSubjects failed:", err);
      setError("Problems with server, check later.");
    }
  };

  const getLanguages = async () => {
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/subjects/list-languages`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (response.ok) {
        const json = await response.json();
        setLanguages(json);
      }
    } catch (err) {
      console.error("getLanguages failed:", err);
    }
  };

  useEffect(() => {
    getSubjects();
    getLanguages();
  }, []);

  function getMap() {
    if (!itemsRef.current) {
      itemsRef.current = new Map();
    }

    return itemsRef.current;
  }

  function scrollToId(itemId) {
    if (!itemId) return;

    const frames = [
      { transform: "translateX(1.2em)" },
      { transform: "translateX(-1.2em)" },
      { transform: "translateX(0)" },
    ];

    const map = getMap();
    const node = map.get(itemId);

    if (node) {
      node.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });

      node.animate(frames, {
        duration: 900,
        fill: "forwards",
        iterations: 1,
      });
    }
  }

  const logout = () => {
    localStorage.removeItem("token");
    setAuth({
      username: "guest",
      email: null,
      role: "guest",
      isGuest: true,
    });
  };

  const openProfile = () => {
    if (!isGuest && auth?.username) {
      setProfile(true);
      return;
    }

    safeOpenLogin();
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  if (userProfile) {
    return <UserProfile successHandler={(state) => setProfile(state)} />;
  }

  if (adminPage) {
    return <AccessInterface successHandler={(state) => setAdmin(state)} />;
  }

  if (creatorPage) {
    return <BookCase successHandler={(state) => setCreator(state)} />;
  }

  if (presentationsPage) {
    return (
      <YourPresentations successHandler={(state) => setPresentations(state)} />
    );
  }

  if (communityCommentsPage) {
    return (
      <CommunityCommentsBrowser
        auth={auth}
        successHandler={(state) => setCommunityCommentsPage(state)}
      />
    );
  }

  if (howtoPage) {
    return <HowToPage successHandler={(state) => setHowTo(state)} />;
  }

  if (aboutPage) {
    return <About successHandler={(state) => setAbout(state)} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-10">
        <div className="mx-auto max-w-xl rounded-[28px] border border-red-200 bg-red-50 px-6 py-5 text-xl font-black text-red-700 shadow-sm">
          {error}
        </div>
      </div>
    );
  }

  if (subject) {
    return subject.type === "Audiobook" ? (
      <Audiobook
        subject={subject}
        exerciseHandler={(state) => {
          setSubject(state);
        }}
      />
    ) : (
      <Videobook
        subject={subject}
        exerciseHandler={(state) => {
          setSubject(state);
        }}
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-xl">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/"
              onClick={() => {
                setSubject();
                setMobileMenuOpen(false);
              }}
              className="group min-w-0"
            >
              <p className="truncate font-serif text-2xl font-light tracking-tight text-slate-900 transition-colors group-hover:text-blue-700">
                The Catchphraser
              </p>
            </Link>

            <button
              type="button"
              className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-300 bg-white shadow-sm transition hover:bg-slate-50 md:hidden"
              onClick={() => setMobileMenuOpen((value) => !value)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
            >
              <div className="flex h-5 w-6 flex-col justify-between">
                <span
                  className={cx(
                    "h-0.5 w-6 rounded-full bg-slate-800 transition-all",
                    mobileMenuOpen && "translate-y-2 rotate-45"
                  )}
                />
                <span
                  className={cx(
                    "h-0.5 w-6 rounded-full bg-slate-800 transition-all",
                    mobileMenuOpen && "opacity-0"
                  )}
                />
                <span
                  className={cx(
                    "h-0.5 w-6 rounded-full bg-slate-800 transition-all",
                    mobileMenuOpen && "-translate-y-2 -rotate-45"
                  )}
                />
              </div>
            </button>

            <div className="hidden flex-1 items-center justify-between gap-4 md:flex">
              <nav className="flex flex-wrap items-center gap-2">
                <HeaderButton
                  primary={!isGuest}
                  onClick={() => setCommunityCommentsPage(true)}
                >
                  Community
                </HeaderButton>

                {auth?.role === "ADMIN" && (
                  <HeaderButton onClick={() => setAdmin(true)}>
                    Admin
                  </HeaderButton>
                )}

                {["CREATOR", "ADMIN"].includes(auth?.role) && (
                  <HeaderButton onClick={() => setCreator(true)}>
                    Creator
                  </HeaderButton>
                )}

                {!isGuest && auth?.username && (
                  <HeaderButton onClick={() => setPresentations(true)}>
                    Presentations
                  </HeaderButton>
                )}

                <HeaderButton onClick={() => setHowTo(true)}>
                  How-to
                </HeaderButton>

                <HeaderButton onClick={() => setAbout(true)}>
                  About
                </HeaderButton>
              </nav>

              <div className="flex shrink-0 items-center gap-2 border-l border-slate-200 pl-4">
                {isGuest ? (
                  <>
                    <HeaderButton onClick={safeOpenLogin}>
                      Sign in
                    </HeaderButton>

                    <HeaderButton primary onClick={safeOpenRegister}>
                      Create account
                    </HeaderButton>
                  </>
                ) : (
                  <>
                    <UserButton
                      auth={auth}
                      message={message}
                      onProfile={openProfile}
                    />

                    <HeaderButton onClick={logout}>
                      Logout
                    </HeaderButton>
                  </>
                )}
              </div>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="mt-4 rounded-[28px] border border-slate-200 bg-white p-3 shadow-xl md:hidden">
              <div className="grid gap-2">
                <HeaderButton
                  mobile
                  primary={!isGuest}
                  onClick={() => {
                    setCommunityCommentsPage(true);
                    closeMobileMenu();
                  }}
                >
                  Community
                </HeaderButton>

                {auth?.role === "ADMIN" && (
                  <HeaderButton
                    mobile
                    onClick={() => {
                      setAdmin(true);
                      closeMobileMenu();
                    }}
                  >
                    Admin
                  </HeaderButton>
                )}

                {["CREATOR", "ADMIN"].includes(auth?.role) && (
                  <HeaderButton
                    mobile
                    onClick={() => {
                      setCreator(true);
                      closeMobileMenu();
                    }}
                  >
                    Creator
                  </HeaderButton>
                )}

                {!isGuest && auth?.username && (
                  <HeaderButton
                    mobile
                    onClick={() => {
                      setPresentations(true);
                      closeMobileMenu();
                    }}
                  >
                    Presentations
                  </HeaderButton>
                )}

                <HeaderButton
                  mobile
                  onClick={() => {
                    setHowTo(true);
                    closeMobileMenu();
                  }}
                >
                  How-to
                </HeaderButton>

                <HeaderButton
                  mobile
                  onClick={() => {
                    setAbout(true);
                    closeMobileMenu();
                  }}
                >
                  About
                </HeaderButton>

                <div className="mt-2 border-t border-slate-100 pt-2">
                  {isGuest ? (
                    <>
                      <HeaderButton
                        mobile
                        onClick={() => {
                          safeOpenLogin();
                          closeMobileMenu();
                        }}
                      >
                        Sign in
                      </HeaderButton>

                      <div className="mt-2">
                        <HeaderButton
                          mobile
                          primary
                          onClick={() => {
                            safeOpenRegister();
                            closeMobileMenu();
                          }}
                        >
                          Create account
                        </HeaderButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <HeaderButton
                        mobile
                        onClick={() => {
                          openProfile();
                          closeMobileMenu();
                        }}
                      >
                        {message || auth.username}
                      </HeaderButton>

                      <div className="mt-2">
                        <HeaderButton
                          mobile
                          onClick={() => {
                            logout();
                            closeMobileMenu();
                          }}
                        >
                          Logout
                        </HeaderButton>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex flex-1 flex-col lg:flex-row">
        <aside
          className={cx(
            "flex-shrink-0 border-r border-slate-200 bg-white lg:w-64 xl:w-72",
            isMobile ? "hidden" : "block"
          )}
        >
          <div className="sticky top-[89px] h-[calc(100vh-89px)] overflow-y-auto px-4 py-6">
            <div className="mb-5 border-b border-slate-200 px-3 pb-4">
              <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                Browse
              </div>

              <p className="mt-1 text-xl font-black text-slate-900">
                Languages
              </p>
            </div>

            {languages && (
              <div className="space-y-1">
                {languages.map((category) => {
                  const cat = category.split("_")[0];
                  const keyValue = `index-${category}`;
                  const flag = getLanguageFlag(cat);

                  return (
                    <div key={keyValue} className="group">
                      <button
                        type="button"
                        className="w-full rounded-2xl border border-transparent px-3 py-3 text-left text-sm font-bold text-slate-700 transition-all duration-200 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-950"
                        onClick={() => scrollToId(category)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 flex-1 items-center gap-3">
                            <span className="shrink-0 text-base">{flag}</span>
                            <span className="truncate">{cat}</span>
                          </div>

                          <svg
                            className="h-3 w-3 shrink-0 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-slate-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        <section className="min-w-0 flex-1">
          <div className="space-y-8 p-5 sm:p-6 md:p-8 lg:p-10">
            {isGuest ? (
              <GuestWelcomeBlock
                onRequireLogin={safeOpenLogin}
                onRegister={safeOpenRegister}
                onHowTo={() => setHowTo(true)}
                onAbout={() => setAbout(true)}
              />
            ) : (
              <LoggedInHeroBlock
                onCommunity={() => setCommunityCommentsPage(true)}
              />
            )}

            {languages &&
              languages.map((item, i) => {
                const key = `${item}-${i}`;

                return (
                  <div
                    key={key}
                    className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md"
                    ref={(node) => {
                      const map = getMap();

                      if (node) {
                        map.set(item, node);
                      } else {
                        map.delete(item);
                      }
                    }}
                  >
                    <div className="p-6 md:p-8">
                      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 pb-5">
                        <div>
                          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
                            {getLanguageFlag(item.split("_")[0])} Language
                          </div>

                          <p className="mt-1 text-2xl font-black text-slate-900">
                            {item} Collection
                          </p>
                        </div>
                      </div>

                      <div className="pt-6">
                        <Collection
                          language={item}
                          itemHandler={(state) => setSubject(state)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Library;